import { useEffect, useRef, useState } from 'react';
import { isIOS } from 'react-device-detect';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../ampli';
import { Button } from '../../../components/common/buttons/Button';
import { MainHead } from '../../../components/common/head/MainHead';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useGetDownloadAppModalHeight } from '../../../hooks/modal/useGetDownloadAppModalHeight';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useAppDispatch } from '../../../hooks/store/useStore';
import { filterSlice } from '../../../store/filterSlice';
import { RecommendedSearchKeyword } from './components/RecommendedSearchKeyword';

const POPULAR_MODELS = [
  '존디어 6155R',
  '존디어 6120R',
  '구보다 M5',
  '얀마 YT5113A',
  '얀마 YT490A',
  '대동 RX730',
  'TYM TX553',
  'LS XU6168',
];

const POPULAR_BRANDS = ['존디어', '얀마', '구보다', '대동', '뉴홀랜드', 'TYM(국제)', 'LS'];

const filterSuggestions = [
  { filterKey: 'price', title: '가격대로 보기' },
  { filterKey: 'engineOutputInPs', title: '마력대로 보기' },
  { filterKey: 'filter_brand', title: '브랜드별로 보기' },
] as const;

export const OpenMarketSearchPage = () => {
  const [searchParams] = useSearchParams();
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const downloadAppModalHeight = useGetDownloadAppModalHeight();

  useLayout({
    fixedHeader: (
      <MainHead
        backButton={{ isShow: true }}
        searchInput={{
          searchKeyword,
          setSearchKeyword,
          inputRef: searchInputRef,
          searchInputType: 'openMarket',
        }}
      />
    ),
    fixedHeaderHeight: 48,
    fixedFooter: <></>,
    fixedFooterHeight: 0,
  });

  useEffect(() => {
    const keyword = searchParams.get('keyword');
    setSearchKeyword(keyword ?? '');
  }, [searchParams]);

  useEffect(() => {
    const inputAutoFocusTimer = setTimeout(() => {
      if (!isIOS) {
        searchInputRef.current?.blur();
        searchInputRef.current?.focus();
      }
    }, 100);

    return () => clearTimeout(inputAutoFocusTimer);
  }, []);

  const handleContainerTouchStart = (e: TouchEvent) => {
    if (searchInputRef.current && !searchInputRef.current.contains(e.target as Node)) {
      searchInputRef.current.blur();
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) container.addEventListener('touchstart', handleContainerTouchStart);

    return () => {
      if (container) container.removeEventListener('touchstart', handleContainerTouchStart);
    };
  }, []);

  const handleFilterSuggestionClick = (filterKey: string, title: string) => {
    dispatch(filterSlice.actions.resetAllFilters({ filterType: 'directDealFilter' }));
    dispatch(filterSlice.actions.setSelectedFilterKey({ filterType: 'directDealFilter', value: filterKey }));

    ampli.pvmClickOpenMarketSearchWatchByFilterButton({ button_name: title });

    // setState가 완료된 후 navigate
    setTimeout(() => {
      navigate(pageInfo.openMarketListPage.path, { replace: true });
    }, 100);
  };

  return (
    <>
      <Helmet>
        <title>{pageInfo.openMarketSearchPage.title}</title>
      </Helmet>

      <Container ref={containerRef} downloadAppModalHeight={downloadAppModalHeight}>
        {!searchKeyword && (
          <ContentsContainer>
            <Section>
              <SectionTitle>🔥 인기 모델</SectionTitle>
              <PillButtonContainer>
                {POPULAR_MODELS.map((model) => (
                  <PillButton
                    key={model}
                    onClick={() => {
                      ampli.pvmClickOpenMarketSearchRecommendedSearchKeyword({
                        type: '모델',
                        title: model,
                      });
                      navigate(`${pageInfo.openMarketSearchResultPage.path}?keyword=${model}`);
                    }}
                  >
                    {model}
                  </PillButton>
                ))}
              </PillButtonContainer>
            </Section>

            <Section>
              <SectionTitle>⭐ 인기 브랜드</SectionTitle>
              <PillButtonContainer>
                {POPULAR_BRANDS.map((brand) => (
                  <PillButton
                    key={brand}
                    onClick={() => {
                      ampli.pvmClickOpenMarketSearchRecommendedSearchKeyword({
                        type: '브랜드',
                        title: brand,
                      });
                      navigate(`${pageInfo.openMarketSearchResultPage.path}?keyword=${brand}`);
                    }}
                  >
                    {brand}
                  </PillButton>
                ))}
              </PillButtonContainer>
            </Section>

            <Section>
              <SectionTitle>🔍 이렇게 찾아 보는건 어때요?</SectionTitle>
              <FilterButtonContainer>
                {filterSuggestions.map(({ filterKey, title }) => (
                  <Button
                    key={filterKey}
                    borderType={'round'}
                    color={'outlinedGreyLight'}
                    size={'md'}
                    onClick={() => handleFilterSuggestionClick(filterKey, title)}
                    text={{
                      content: title,
                      size: 'md',
                    }}
                    icon={{
                      position: 'right',
                      fileName: 'AngleRight',
                      size: 'Medium',
                      fill: '#505363',
                    }}
                  />
                ))}
              </FilterButtonContainer>
            </Section>
          </ContentsContainer>
        )}

        {searchKeyword && <RecommendedSearchKeyword searchKeyword={searchKeyword} />}
      </Container>
    </>
  );
};

const Container = styled.div<{ downloadAppModalHeight: number }>`
  width: 100%;
  min-height: ${({ downloadAppModalHeight }) => `calc(100vh - 48px - ${downloadAppModalHeight}px)`};
`;

const ContentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px 16px;
  gap: 40px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-self: stretch;
`;

const SectionTitle = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%;
`;

const PillButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PillButton = styled.button`
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;

  cursor: pointer;

  &:active {
    background: var(--Light-Scheme-Monochrome-grey20, #ececf3);
  }
`;

const FilterButtonContainer = styled.div`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
`;
